import { useState } from 'react';
import Banner from '@savvy-kit/patterns/banner';
import { AggregatorConnectButton } from 'src/components/aggregator/aggregator-connect-button';
import { DISCONNECTED_SOURCE_STATUSES } from 'src/utils/account';

import type { ID } from '@savvy/utils/id';

type ProviderAccountInfo = {
  readonly id: ID;
  readonly source?: string | null;
  readonly sourceStatus?: string | null;
  readonly providerName?: string | null;
};

function isDisconnected(pa: ProviderAccountInfo): boolean {
  return DISCONNECTED_SOURCE_STATUSES.includes(pa.sourceStatus ?? '');
}

type DisconnectedAccountsBannerProps = {
  readonly providerAccounts: readonly ProviderAccountInfo[] | null | undefined;
  readonly onDismiss: () => void;
};

export default function DisconnectedAccountsBanner({ providerAccounts, onDismiss }: DisconnectedAccountsBannerProps) {
  const disconnected = [...(providerAccounts?.filter(isDisconnected) ?? [])].sort((a, b) =>
    String(a.id).localeCompare(String(b.id)),
  );
  const [isLoading, setIsLoading] = useState(false);

  if (disconnected.length === 0) return null;

  const single = disconnected.length === 1;
  const firstPA = disconnected.at(0);
  const institutionName = firstPA?.providerName?.trim() || null;
  const title = single
    ? 'Your linked account needs attention'
    : `${disconnected.length} linked accounts need attention`;
  const description = single
    ? `Your ${institutionName ?? 'linked'} account has been disconnected. Reconnect to keep your net worth up to date.`
    : 'Some of your linked accounts have been disconnected. Reconnect one now — after reload, you can fix the rest.';
  const reconnectTitle = institutionName ? `Reconnect ${institutionName}` : 'Reconnect';

  return (
    <Banner
      variant="warning"
      title={title}
      description={description}
      data-testid="disconnected-accounts-banner"
      actionButton={
        firstPA ? (
          <AggregatorConnectButton
            providerAccountId={firstPA.id}
            providerAccountSource={firstPA.source}
            setIsLoading={setIsLoading}
            buttonProps={{
              disabled: isLoading,
              size: 'sm',
              className: 'rounded-sm',
            }}
            title={reconnectTitle}
            onSuccess={() => {
              window.location.reload();
              return Promise.resolve();
            }}
          />
        ) : undefined
      }
      handleClickX={onDismiss}
    />
  );
}

export function getDisconnectedProviderAccountIds(
  providerAccounts: readonly ProviderAccountInfo[] | null | undefined,
): string {
  return (providerAccounts ?? [])
    .filter(isDisconnected)
    .map((pa) => pa.id)
    .sort()
    .join(',');
}
